import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, renderList as _renderList, resolveComponent as _resolveComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm" }
const _hoisted_3 = { class: "min-w-full divide-y divide-gray-200" }
const _hoisted_4 = {
  key: 0,
  class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
}
const _hoisted_5 = { class: "divide-y divide-gray-200 bg-white" }
const _hoisted_6 = { class: "hover:bg-gray-50 transition-colors duration-150 relative" }
const _hoisted_7 = ["colspan"]
const _hoisted_8 = {
  key: 1,
  class: "px-6 py-4 text-sm"
}
const _hoisted_9 = {
  key: 1,
  class: "text-gray-900"
}
const _hoisted_10 = { class: "whitespace-nowrap px-6 py-4 text-sm text-gray-900" }
const _hoisted_11 = { class: "px-6 py-4 text-sm" }
const _hoisted_12 = { class: "flex flex-wrap gap-1" }
const _hoisted_13 = {
  key: 0,
  class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full opacity-75 bg-gray-400 text-gray-700"
}
const _hoisted_14 = { class: "px-6 py-4 text-sm" }
const _hoisted_15 = { class: "px-6 py-4 text-sm text-gray-900" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_18 = { class: "px-6 py-4 text-sm text-gray-900" }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_21 = { class: "px-6 py-4 text-sm text-gray-900" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_24 = { class: "whitespace-nowrap px-6 py-4 text-sm" }
const _hoisted_25 = { class: "relative dropdown-container" }
const _hoisted_26 = ["onClick", "disabled"]
const _hoisted_27 = { class: "flex items-center flex-1" }
const _hoisted_28 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_29 = {
  key: 0,
  class: "absolute z-10 mt-2 w-full min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200"
}
const _hoisted_30 = { class: "py-2 max-h-48 overflow-y-auto" }
const _hoisted_31 = ["onClick", "disabled"]
const _hoisted_32 = {
  key: 0,
  class: "px-3 py-2 text-sm text-gray-500"
}
const _hoisted_33 = { class: "whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 text-center" }
const _hoisted_34 = {
  key: 0,
  class: "bg-gray-50"
}
const _hoisted_35 = ["colspan"]
const _hoisted_36 = ["onClick"]
const _hoisted_37 = { class: "flex items-center gap-2" }
const _hoisted_38 = { class: "mt-2 p-4 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-in-out" }
const _hoisted_39 = { class: "text-sm text-red-700 leading-relaxed whitespace-pre-wrap" }

import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";
import { useNotifications } from "@/composables/useNotifications";
import { generateConfigTitle } from "@/utils/v2";
import { ConfiguredVaccine, getVaccinationConfigurationInstructions, GroupTag, VaccineConfiguration, VaccineMethod } from "@/types/v2";
import { Tank, TankGroup } from "@/types/v2";
import { getVaccinationProductDisplayItems, isLikelyBathVaccinationConfiguration } from "@/utils/v2/tasks/display";


import JustificationModal from "@/components/v2/Modal/JustificationModal.vue";

interface Props {
  configurations: VaccineConfiguration[],
  vaccineMethods: VaccineMethod[],
  tanks: Tank[]
}

// eslint-disable-next-line no-undef
type VaccineUpdateKey = keyof ConfiguredVaccine;
type ConfigUpdateKey = keyof VaccineConfiguration;
type UpdateKey = VaccineUpdateKey | ConfigUpdateKey;


export default /*@__PURE__*/_defineComponent({
  __name: 'ConfigurationsTable',
  props: {
    configurations: {},
    vaccineMethods: {},
    tanks: {}
  },
  setup(__props: any) {

const props = __props;

const { updateConfiguration } = useVaccinationConfigurations();
const { availableTags } = useVaccination();
const { recordChange } = useTaskHistory();
const { showSuccess, showError } = useNotifications();

// Local state
const tankGroups = ref<Record<string, TankGroup[]>>({});
const selectedTags = ref<Record<number, GroupTag | null>>({});
const isUpdating = ref<Record<number, boolean>>({});

// Local state for instructions visibility
const showInstructions = ref<Record<number, boolean>>({});

// Initialize instructions as expanded by default
watch(() => props.configurations, (configs) => {
    if (configs) {
        configs.forEach((config, index) => {
            if (getVaccinationConfigurationInstructions(config) && showInstructions.value[index] === undefined) {
                showInstructions.value[index] = true;
            }
        });
    }
}, { immediate: true });

// Toggle instructions visibility
const toggleInstructions = (configIndex: number) => {
  showInstructions.value[configIndex] = !showInstructions.value[configIndex];
};

// Helper to find the new tag (tag in destinationGroup but not in sourceGroup)
const findNewTag = (config: VaccineConfiguration): GroupTag | null => {
  const sourceTags = config.sourceGroup?.tags || [];
  const destinationTags = config.destinationGroup?.tags || [];
  
  // Find tags in destination that are not in source (excluding Start Tag)
  const sourceTagTitles = new Set(
    sourceTags
      .filter(tag => tag.title !== 'Start Tag')
      .map(tag => tag.title)
  );
  
  const newTag = destinationTags.find(tag => 
    tag.title !== 'Start Tag' && !sourceTagTitles.has(tag.title)
  );
  
  return newTag || null;
};

const dropdownOpen = ref<Record<number, boolean>>({});

// Detect if this is a bath configuration
const isBathConfiguration = (config: VaccineConfiguration): boolean => {
  return isLikelyBathVaccinationConfiguration(config);
};

const getVaccinesDisplay = (config: VaccineConfiguration) => {
  return getVaccinationProductDisplayItems(config);
};

// Check if any configuration is a bath configuration
const hasBathConfigurations = computed(() => {
  return props.configurations.some(config => isBathConfiguration(config));
});

const handleFieldChange = async (configIndex: number, event: { index: number; key: UpdateKey; item: any }) => {
  const { index, key, item } = event;
  const config = props.configurations[configIndex];

  const oldValue = config[key as keyof VaccineConfiguration];

  if (JSON.stringify(oldValue) === JSON.stringify(item)) {
    return;
  }

  // Set loading state
  isUpdating.value[configIndex] = true;

  try {
    const requireJustification = ['destinationTank', 'destinationGroup'].includes(key);

    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].${key}`,
      fieldName: getFieldLabel(key),
      oldValue,
      newValue: item,
      context: 'VaccinationConfiguration',
      contextType: 'edit',
      workflowStage: 'setup',
      requireJustification,
      justification: ''
    });

    if (success) {
      await updateConfiguration(configIndex, index, key, item);

      if (key === 'destinationTank') {
        updateTankGroups(configIndex, item);
      }

      // Show success notification
      showSuccess(
        "Configuration Updated", 
        `${getFieldLabel(key)} has been updated successfully.`
      );
    }
  } catch (error) {
    console.error('Error updating configuration:', error);
    showError(
      "Update Failed", 
      `Failed to update ${getFieldLabel(key)}. Please try again.`
    );
  } finally {
    // Clear loading state
    isUpdating.value[configIndex] = false;
  }
};

// Update tank groups when destination tank changes
const updateTankGroups = (configIndex: number, tank: Tank) => {
  const tankWithGroups = props.tanks.find(
      t => t.key === tank?.key
  );

  if (tankWithGroups?.groups) {
    tankGroups.value[configIndex.toString()] = tankWithGroups.groups;
  } else {
    tankGroups.value[configIndex.toString()] = [];
  }

  // Clear destination group when tank changes
  updateConfiguration(configIndex, -1, 'destinationGroup', null);
};

// Format for display purposes
const getFieldLabel = (field: string): string => {
  const fieldLabels: Record<string, string> = {
    'destinationTank': 'Destination Tank',
    'destinationGroup': 'Destination Group',
    'tag': 'Tag Color'
  };

  return fieldLabels[field] || field;
};

// Toggle dropdown
const toggleDropdown = (configIndex: number) => {
  dropdownOpen.value[configIndex] = !dropdownOpen.value[configIndex];
  // Close other dropdowns
  Object.keys(dropdownOpen.value).forEach(key => {
    if (parseInt(key) !== configIndex) {
      dropdownOpen.value[parseInt(key)] = false;
    }
  });
};

// Close dropdown when clicking outside
const closeDropdown = (configIndex: number) => {
  dropdownOpen.value[configIndex] = false;
};

// Close all dropdowns
const closeAllDropdowns = () => {
  Object.keys(dropdownOpen.value).forEach(key => {
    dropdownOpen.value[parseInt(key)] = false;
  });
};

// Handle clicks outside dropdown
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.dropdown-container')) {
    closeAllDropdowns();
  }
};

// Handle tag selection from custom dropdown
const selectTag = async (configIndex: number, tag: GroupTag) => {
  const oldTag = selectedTags.value[configIndex];
  const config = props.configurations[configIndex];
  
  // Set loading state
  isUpdating.value[configIndex] = true;

  try {
    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].selectedTag`,
      fieldName: `Tag (${tag.title})`,
      oldValue: {
        key: oldTag?.key || 'None',
        value: oldTag?.title || 'None',
      },
      newValue: {
        key: tag.key,
        value: tag.title,
      },
      context: 'VaccinationConfiguration',
      contextType: 'edit',
      workflowStage: 'setup',
      requireJustification: true,
      justification: ''
    });

    if (success) {
      // Update the selected tag
      selectedTags.value[configIndex] = tag;
      
      // Update destination group to include the new tag
      // First, get source tags to preserve them
      const sourceTags = config.sourceGroup?.tags || [];
      const sourceTagTitles = new Set(
        sourceTags
          .filter(t => t.title !== 'Start Tag')
          .map(t => t.title)
      );
      
      // Create updated destination group with source tags + new tag
      const updatedDestGroup = config.destinationGroup
        ? { ...config.destinationGroup }
        : { key: '', name: '', tags: [] } as TankGroup;
      
      if (!updatedDestGroup.tags) {
        updatedDestGroup.tags = [];
      } else {
        updatedDestGroup.tags = [...updatedDestGroup.tags];
      }
      
      // Remove old new tag (tag that was in destination but not in source)
      updatedDestGroup.tags = updatedDestGroup.tags.filter(t => 
        sourceTagTitles.has(t.title) || t.title === tag.title
      );
      
      // Add new tag if it doesn't exist
      const tagExists = updatedDestGroup.tags.some(t => t.title === tag.title);
      if (!tagExists) {
        updatedDestGroup.tags.push(tag);
      }
      
      // Update the destination group
      await updateConfiguration(configIndex, -1, 'destinationGroup', updatedDestGroup);
      
      // Show success notification
      showSuccess(
        "Tag Updated", 
        `Tag has been changed to "${tag.title}".`
      );
    }
  } catch (error) {
    console.error('Error updating tag:', error);
    showError(
      "Tag Update Failed", 
      "Failed to update tag. Please try again."
    );
  } finally {
    // Clear loading state
    isUpdating.value[configIndex] = false;
  }
  
  closeDropdown(configIndex);
};

// Watch for configuration changes to load tank groups and initialize selected tags
watch(() => props.configurations, (configs) => {
  if (configs) {
    configs.forEach((config, index) => {
      // Load tank groups
      if (config.destinationTank?.key) {
        const tankWithGroups = props.tanks.find(
            t => t.key === config.destinationTank.key
        );
        if (tankWithGroups?.groups) {
          tankGroups.value[index.toString()] = tankWithGroups.groups;
        }
      }
      
      // Initialize selected tag from destination group (new tag)
      if (!selectedTags.value[index]) {
        const newTag = findNewTag(config);
        if (newTag) {
          selectedTags.value[index] = newTag;
        }
      }
    });
  }
}, { immediate: true });

// Add/remove event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

return (_ctx: any,_cache: any) => {
  const _component_ItemDropdown = _resolveComponent("ItemDropdown")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("table", _hoisted_3, [
        _createElementVNode("thead", null, [
          _createElementVNode("tr", null, [
            (hasBathConfigurations.value)
              ? (_openBlock(), _createElementBlock("th", _hoisted_4, "Tank"))
              : _createCommentVNode("", true),
            (!hasBathConfigurations.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _cache[0] || (_cache[0] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Source Tank", -1)),
                  _cache[1] || (_cache[1] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Source Group", -1)),
                  _cache[2] || (_cache[2] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Destination Tank", -1))
                ], 64))
              : _createCommentVNode("", true),
            _cache[3] || (_cache[3] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "IVP", -1)),
            _cache[4] || (_cache[4] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Method", -1)),
            _cache[5] || (_cache[5] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Dose (mL)", -1)),
            _cache[6] || (_cache[6] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Tag", -1)),
            _cache[7] || (_cache[7] = _createElementVNode("th", { class: "bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500" }, "Target Amount", -1))
          ])
        ]),
        _createElementVNode("tbody", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.configurations, (config, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: config.key
            }, [
              _createElementVNode("tr", _hoisted_6, [
                (isUpdating.value[index])
                  ? (_openBlock(), _createElementBlock("td", {
                      key: 0,
                      colspan: hasBathConfigurations.value ? 6 : 8,
                      class: "absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10"
                    }, _cache[8] || (_cache[8] = [
                      _createStaticVNode("<div class=\"flex items-center space-x-2\"><svg class=\"animate-spin h-5 w-5 text-blue-600\" xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\"><circle class=\"opacity-25\" cx=\"12\" cy=\"12\" r=\"10\" stroke=\"currentColor\" stroke-width=\"4\"></circle><path class=\"opacity-75\" fill=\"currentColor\" d=\"M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z\"></path></svg><span class=\"text-sm text-gray-600\">Updating...</span></div>", 1)
                    ]), 8, _hoisted_7))
                  : _createCommentVNode("", true),
                (hasBathConfigurations.value)
                  ? (_openBlock(), _createElementBlock("td", _hoisted_8, [
                      (isBathConfiguration(config))
                        ? (_openBlock(), _createBlock(_component_ItemDropdown, {
                            key: 0,
                            index: -1,
                            label: '',
                            items: _ctx.tanks,
                            itemKey: 'sourceTank',
                            selectedItem: config.sourceTank || config.destinationTank,
                            disabled: isUpdating.value[index],
                            onFieldChange: (event) => handleFieldChange(index, event)
                          }, null, 8, ["items", "selectedItem", "disabled", "onFieldChange"]))
                        : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(config.sourceTank?.name || config.sourceTank?.label || 'None'), 1))
                    ]))
                  : _createCommentVNode("", true),
                (!hasBathConfigurations.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      _createElementVNode("td", _hoisted_10, _toDisplayString(config.sourceTank?.name || config.sourceTank?.label || 'None'), 1),
                      _createElementVNode("td", _hoisted_11, [
                        _createElementVNode("div", _hoisted_12, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((config.sourceGroup?.tags || []).filter((tag) => tag.title !== 'Start Tag'), (tag) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: tag.key,
                              class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full",
                              style: _normalizeStyle({
                    backgroundColor: tag.backgroundColor || '#e5e7eb',
                    color: tag.textColor || '#374151'
                  })
                            }, _toDisplayString(tag.title), 5))
                          }), 128)),
                          (!config.sourceGroup?.tags || config.sourceGroup.tags.filter((tag) => tag.title !== 'Start Tag').length === 0)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_13, " Untagged "))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _createElementVNode("td", _hoisted_14, [
                        _createVNode(_component_ItemDropdown, {
                          index: -1,
                          label: '',
                          items: _ctx.tanks,
                          itemKey: 'destinationTank',
                          selectedItem: config.destinationTank,
                          disabled: isUpdating.value[index],
                          onFieldChange: (event) => handleFieldChange(index, event)
                        }, null, 8, ["items", "selectedItem", "disabled", "onFieldChange"])
                      ])
                    ], 64))
                  : _createCommentVNode("", true),
                _createElementVNode("td", _hoisted_15, [
                  (getVaccinesDisplay(config).length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getVaccinesDisplay(config), (vaccine, vIdx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: vIdx,
                            class: "mb-1"
                          }, _toDisplayString(vaccine.name), 1))
                        }), 128))
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_17, "None"))
                ]),
                _createElementVNode("td", _hoisted_18, [
                  (getVaccinesDisplay(config).length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getVaccinesDisplay(config), (vaccine, vIdx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: vIdx,
                            class: "mb-1"
                          }, _toDisplayString(vaccine.method), 1))
                        }), 128))
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_20, "None"))
                ]),
                _createElementVNode("td", _hoisted_21, [
                  (getVaccinesDisplay(config).length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getVaccinesDisplay(config), (vaccine, vIdx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: vIdx,
                            class: "mb-1"
                          }, _toDisplayString(vaccine.dose), 1))
                        }), 128))
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_23, "N/A"))
                ]),
                _createElementVNode("td", _hoisted_24, [
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("button", {
                      onClick: ($event: any) => (toggleDropdown(index)),
                      disabled: isUpdating.value[index],
                      class: "flex items-center justify-between w-full min-w-[150px] text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    }, [
                      _createElementVNode("div", _hoisted_27, [
                        (selectedTags.value[index])
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full",
                              style: _normalizeStyle({ 
                      backgroundColor: selectedTags.value[index]?.backgroundColor || '#e5e7eb', 
                      color: selectedTags.value[index]?.textColor || '#374151' 
                    })
                            }, _toDisplayString(selectedTags.value[index]?.title), 5))
                          : (_openBlock(), _createElementBlock("span", _hoisted_28, "Select a tag..."))
                      ]),
                      _cache[9] || (_cache[9] = _createElementVNode("svg", {
                        class: "h-4 w-4 text-gray-500 ml-2 flex-shrink-0",
                        xmlns: "http://www.w3.org/2000/svg",
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M19 9l-7 7-7-7"
                        })
                      ], -1))
                    ], 8, _hoisted_26),
                    (dropdownOpen.value[index])
                      ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                          _createElementVNode("div", _hoisted_30, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableTags).filter((tag) => tag.title !== 'Start Tag'), (tag) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: tag.key,
                                onClick: ($event: any) => (selectTag(index, tag)),
                                disabled: isUpdating.value[index],
                                class: "w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                              }, [
                                _createElementVNode("span", {
                                  class: "inline-block px-2 py-1 text-xs font-medium rounded-full",
                                  style: _normalizeStyle({ 
                        backgroundColor: tag.backgroundColor || '#e5e7eb', 
                        color: tag.textColor || '#374151' 
                      })
                                }, _toDisplayString(tag.title), 5)
                              ], 8, _hoisted_31))
                            }), 128)),
                            (_unref(availableTags).filter((tag) => tag.title !== 'Start Tag').length === 0)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_32, " No tags available "))
                              : _createCommentVNode("", true)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("td", _hoisted_33, _toDisplayString(config.targetAmount || 0), 1)
              ]),
              (_unref(getVaccinationConfigurationInstructions)(config))
                ? (_openBlock(), _createElementBlock("tr", _hoisted_34, [
                    _createElementVNode("td", {
                      colspan: hasBathConfigurations.value ? 6 : 8,
                      class: _normalizeClass(showInstructions.value[index] ? 'px-6 py-4' : 'px-6 py-1.5')
                    }, [
                      _createElementVNode("button", {
                        onClick: ($event: any) => (toggleInstructions(index)),
                        class: _normalizeClass(["w-full flex items-center justify-between text-left hover:bg-gray-100 rounded transition-colors duration-200", showInstructions.value[index] ? 'p-3' : 'p-1.5'])
                      }, [
                        _createElementVNode("div", _hoisted_37, [
                          _cache[10] || (_cache[10] = _createElementVNode("svg", {
                            class: "w-4 h-4 text-red-500 flex-shrink-0",
                            fill: "currentColor",
                            viewBox: "0 0 20 20"
                          }, [
                            _createElementVNode("path", {
                              "fill-rule": "evenodd",
                              d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
                              "clip-rule": "evenodd"
                            })
                          ], -1)),
                          _createElementVNode("span", {
                            class: _normalizeClass(showInstructions.value[index] ? 'text-sm font-semibold text-red-600' : 'text-xs font-semibold text-red-600')
                          }, " Special Instructions ", 2)
                        ]),
                        (_openBlock(), _createElementBlock("svg", {
                          class: _normalizeClass(["w-4 h-4 text-gray-500 transition-transform duration-200", { 'rotate-180': showInstructions.value[index] }]),
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24"
                        }, _cache[11] || (_cache[11] = [
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "2",
                            d: "M19 9l-7 7-7-7"
                          }, null, -1)
                        ]), 2))
                      ], 10, _hoisted_36),
                      _withDirectives(_createElementVNode("div", _hoisted_38, [
                        _createElementVNode("p", _hoisted_39, _toDisplayString(_unref(getVaccinationConfigurationInstructions)(config)), 1)
                      ], 512), [
                        [_vShow, showInstructions.value[index]]
                      ])
                    ], 10, _hoisted_35)
                  ]))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ])
      ])
    ]),
    _createVNode(JustificationModal)
  ]))
}
}

})